import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, Leaf, RefreshCcw, Sparkles, Truck } from "lucide-react";
import useSWR from "swr";
import { SparkMark } from "./SparkLogo";
import { IMAGES } from "../data/site";
import { API, fetcher } from "../lib/api";

const easeOut = [0.16, 1, 0.3, 1];

const lineReveal = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.95, ease: easeOut, delay: 0.12 + i * 0.13 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), {
    stiffness: 120,
    damping: 18,
  });

  const handleMouse = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const { data: products = [] } = useSWR(`${API}/products`, fetcher);
  const scenes = useMemo(() => {
    const preferred = [
      ["Flower Girl 3-Piece Set", "Everyday comfort"],
      ["Cloud Panda Onesie", "Gentle babywear"],
      ["Supercute 2-in-1", "School & play"],
      ["Custom Cartoon Tank Top", "Bright & comfy"],
      ["Green & Red High Bow", "Festive sparkle"],
    ];
    const list = [{ image: IMAGES.heroMain, alt: "Happy children wearing Sparkwear", caption: "Welcome to Sparkwear" }];
    for (const [name, caption] of preferred) {
      const p = products.find((x) => x.name?.startsWith(name) && x.image);
      if (p) list.push({ image: p.image, alt: `${p.name} — Sparkwear`, caption });
    }
    return list;
  }, [products]);

  const [scene, setScene] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setScene((s) => (s + 1) % scenes.length), 3800);
    return () => clearInterval(t);
  }, [scenes.length]);
  const current = scenes[scene] || { image: IMAGES.heroMain, alt: "Sparkwear", caption: "Welcome to Sparkwear" };

  return (
    <section
      ref={ref}
      onMouseMove={handleMouse}
      className="relative overflow-hidden pb-14 pt-28 md:pb-20 md:pt-36"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-gradient-to-br from-blush-100 to-lav-100 opacity-80 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-8%] h-[420px] w-[420px] rounded-full bg-gradient-to-tr from-mint-50 to-baby-50 opacity-90 blur-3xl" />
      <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.05]" />

      <Sparkles className="pointer-events-none absolute left-[7%] top-[24%] hidden h-8 w-8 animate-twinkle text-blush-500 md:block" />
      <Sparkles className="pointer-events-none absolute left-[46%] top-[15%] hidden h-5 w-5 animate-twinkle text-butter-500 md:block" style={{ animationDelay: "0.6s" }} />
      <Sparkles className="pointer-events-none absolute right-[5%] top-[40%] hidden h-7 w-7 animate-twinkle text-lav-500 md:block" style={{ animationDelay: "1.1s" }} />
      <Sparkles className="pointer-events-none absolute bottom-[10%] right-[30%] hidden h-6 w-6 animate-twinkle text-baby-500 md:block" style={{ animationDelay: "1.6s" }} />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="inline-flex items-center gap-2 rounded-full border border-blush-300/60 bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.22em] text-blush-700 backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Welcome to Sparkwear
          </motion.div>

          <h1 className="mt-6 font-display text-[3.3rem] font-semibold leading-[0.98] tracking-tight text-ink sm:text-7xl lg:text-[5.4rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                variants={lineReveal}
                custom={0}
                initial="hidden"
                animate="show"
              >
                Where
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                variants={lineReveal}
                custom={1}
                initial="hidden"
                animate="show"
              >
                Kids
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-3">
              <motion.span
                className="text-gradient-spark block"
                variants={lineReveal}
                custom={2}
                initial="hidden"
                animate="show"
              >
                Shine
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.55 }}
            className="mt-6 max-w-md text-base leading-relaxed text-muted md:text-lg"
          >
            Pastel-perfect, play-proof kidswear in sizes 0–10Y. Soft organic
            cotton, happy colours and little details made to shine.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.62 }}
            className="mt-3 font-display text-sm font-semibold uppercase tracking-[0.18em] text-blush-700 md:text-base"
            data-testid="hero-tagline"
          >
            Comfort. Style. Happy Little Sparks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.68 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="shop-now-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-white shadow-lg shadow-ink/20 transition-transform duration-300 hover:scale-[1.03]"
            >
              Shop Now
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <a
              href="#collection"
              data-testid="hero-explore-categories-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-lav-300/70 bg-white/60 px-7 py-[14px] font-display text-base font-semibold text-ink backdrop-blur transition-colors duration-300 hover:bg-white"
            >
              Explore Categories
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-muted"
          >
            <span className="inline-flex items-center gap-2">
              <Truck className="h-4 w-4 text-blush-600" />
              Free shipping over £50
            </span>
            <span className="inline-flex items-center gap-2">
              <RefreshCcw className="h-4 w-4 text-mint-600" />
              30-day happy returns
            </span>
            <span className="inline-flex items-center gap-2">
              <Leaf className="h-4 w-4 text-lav-600" />
              Organic-soft cotton
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative mx-auto w-full max-w-[440px]"
          >
            <motion.div
              style={{ y: yMain }}
              className="relative overflow-hidden rounded-t-[240px] rounded-b-[40px] border-8 border-white shadow-2xl shadow-lav-100"
            >
              <div className="relative h-[420px] w-full md:h-[500px]">
                <AnimatePresence>
                  <motion.img
                    key={scene}
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1.14 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{
                      opacity: { duration: 1.2, ease: "easeInOut" },
                      scale: { duration: 4.6, ease: "linear" },
                    }}
                    src={current.image}
                    alt={current.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink/35 to-transparent" />
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={current.caption}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.45 }}
                      className="inline-block rounded-full bg-white/90 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-ink shadow-sm backdrop-blur"
                      data-testid="hero-scene-caption"
                    >
                      {current.caption}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className="absolute right-4 top-4 flex gap-1.5">
                  {scenes.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === scene ? "w-5 bg-white" : "w-1.5 bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div style={{ y: yCard }} className="absolute -left-4 bottom-10 w-40 md:-left-12 md:w-48">
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -14 }}
                animate={{ opacity: 1, y: 0, rotate: -7 }}
                transition={{ duration: 0.8, ease: easeOut, delay: 0.9 }}
                className="overflow-hidden rounded-3xl border-[6px] border-white shadow-xl"
              >
                <img
                  src={IMAGES.heroSecondary}
                  alt="Kids laughing together in Sparkwear"
                  className="h-32 w-full object-cover md:h-36"
                />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 1.05 }}
              className="absolute -right-2 top-8 md:-right-6"
            >
              <div className="flex h-24 w-24 animate-float flex-col items-center justify-center rounded-full bg-white/95 shadow-lg shadow-lav-100 backdrop-blur">
                <SparkMark size={26} />
                <span className="mt-1.5 px-2 text-center text-[9px] font-bold uppercase tracking-[0.12em] text-ink">
                  100% organic cotton
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 1.2 }}
              className="absolute -right-3 bottom-20 animate-float-slow md:-right-8"
            >
              <span className="inline-block rounded-full bg-ink px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-lg">
                New drops weekly
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
